import { useEffect, useState } from "react";
import { Archive, Lock, Plus, Search, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import JournalForm from "@/pages/JournalForm";
import { AccountFields, CATEGORY_OPTIONS, catLabel, suggestCode, initAccountForm, accountPayload } from "@/components/AccountFields";

export { CATEGORY_OPTIONS, suggestCode };
const todayLabel = () => new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState(null);
  const load = () => {
    api("/accounts?archived=1").then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
    api("/accounts/balances").then(setBalances).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 7000);
    return () => clearTimeout(t);
  }, [message]);
  const visible = rows.filter((a) => (showArchived ? true : a.is_active !== false)).filter((a) => `${a.code} ${a.name} ${a.category}`.toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(visible, 50, `${showArchived}|${search}`);
  const done = (text) => { setView("list"); setEditing(null); setMessage({ text, type: "success" }); load(); };
  const archive = async (a) => {
    try {
      const r = await api(`/accounts/${a.id}/archive`, { method: "POST" });
      setMessage({ text: `Akun ${a.name} ${r.is_active ? "diaktifkan kembali" : "diarsipkan"}`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  const remove = async (a) => {
    try {
      await api(`/accounts/${a.id}`, { method: "DELETE" });
      setMessage({ text: `Akun ${a.name} dihapus`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  if (view === "journal") return <JournalForm onCancel={() => { setView("list"); load(); }} onSaved={(r) => done(`${r.number} berhasil diposting — total ${money(r.total_debit)}`)} />;
  if (view === "form") return <AccountForm accounts={rows} initial={editing} onCancel={() => { setView("list"); setEditing(null); load(); }} onSaved={(r, isEdit) => done(`Akun ${r.code} · ${r.name} berhasil ${isEdit ? "diubah" : "dibuat"}`)} />;
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">AKUN</p><h1>Daftar Akun</h1></div>
        <div className="row-actions">
          <button data-testid="accounts-create-journal" className="secondary-btn" onClick={() => setView("journal")}><Plus size={15} /> Buat Jurnal Umum</button>
          <button data-testid="accounts-create-account" className="primary-btn" onClick={() => setView("form")}><Plus size={15} /> Buat Akun Baru</button>
        </div>
      </div>
      {message && <div data-testid={message.type === "error" ? "accounts-error-message" : "accounts-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="acc-toolbar">
        <label className="exp-check"><input type="checkbox" data-testid="accounts-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Tampilkan Arsip Akun</label>
        <div className="workflow-search prod-search"><Search size={16} /><input data-testid="accounts-search-input" placeholder="Cari akun..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="acc-note">Saldo di bawah berdasarkan tanggal {todayLabel()}, kecuali ada pernyataan lain</span>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table acc-table" data-testid="accounts-table">
          <thead><tr><th className="check-col"><input type="checkbox" disabled /></th><th className="acc-lock-col">KUNCI</th><th>KODE AKUN</th><th>NAMA AKUN</th><th>KATEGORI AKUN</th><th>PENGGUNA</th><th>PAJAK</th><th className="right">SALDO (DALAM IDR)</th><th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((a) => (
              <tr key={a.id} className={a.is_active === false ? "acc-archived" : ""} data-testid={`account-row-${a.code}`}>
                <td className="check-col"><input type="checkbox" disabled={a.locked} /></td>
                <td className="acc-lock-col">{a.locked ? <Lock size={13} className="acc-lock" /> : <span className="muted">–</span>}</td>
                <td className="mono">{a.code}</td>
                <td><span className="cb-account-link">{a.name}</span>{a.is_active === false && <span className="stock-chip out">Arsip</span>}</td>
                <td>{catLabel(a.category)}</td>
                <td>all</td>
                <td>{a.tax || ""}</td>
                <td className="right mono">{money(balances[a.code] || 0)}</td>
                <td>
                  <span className="row-actions">
                    <button data-testid={`account-edit-${a.code}`} className="table-action" onClick={() => { setEditing(a); setView("form"); }}>Edit</button>
                    {!a.locked && <button data-testid={`account-archive-${a.code}`} className="table-action" onClick={() => archive(a)}>{a.is_active === false ? "Aktifkan" : "Arsipkan"}</button>}
                    {!a.locked && <button data-testid={`account-delete-${a.code}`} className="table-action danger" onClick={() => remove(a)}>Hapus</button>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination pager={pager} testid="accounts-pagination" />
      </div>
    </div>
  );
}

function AccountForm({ accounts, initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initAccountForm(initial, accounts));
  const [isSub, setIsSub] = useState(Boolean(initial?.parent_code));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = accountPayload(form, isSub);
      const r = initial
        ? await api(`/accounts/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await api("/accounts", { method: "POST", body: JSON.stringify(payload) });
      onSaved(r, Boolean(initial));
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="workflow-page" data-testid="account-form-page">
      <div className="page-heading">
        <div><p className="eyebrow">DAFTAR AKUN</p><h1>{initial ? "Edit Akun" : "Tambah akun baru"}</h1></div>
      </div>
      {error && <div className="success-message error" data-testid="account-form-error">{error}<button onClick={() => setError("")}><X size={14} /></button></div>}
      <div className="surface exp-form-wrap">
        <AccountFields accounts={accounts} form={form} setForm={setForm} isSub={isSub} setIsSub={setIsSub} initial={initial} />
        <div className="form-actions acc-actions">
          <button data-testid="account-form-cancel" className="text-btn" onClick={onCancel}>Batalkan</button>
          <button data-testid="account-form-save" className="primary-btn" disabled={!form.name || !form.code || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}
